import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Crown, Sparkles, Clock, Check, Wand2 } from "lucide-react";

const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }); } catch { return ""; } };

export function BusinessPlanCard() {
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/billing/me").then((r) => setB(r.data.business)).catch(() => {}); }, []);
  if (!b) return null;

  const price = b.pricing || {};
  const isTrial = b.status === "pro_trial";
  const isPro = b.is_pro;

  return (
    <div data-testid="business-plan-card" className={`mt-6 rounded-2xl border p-5 ${isPro ? "border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" : "border-zinc-200 bg-white"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${isPro ? "bg-indigo-600 text-white" : "bg-zinc-200 text-zinc-500"}`}>
            {isPro ? <Crown className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
          </div>
          <div>
            <p className="font-display text-lg font-bold text-zinc-900" data-testid="plan-card-name">{isPro ? "Business Pro" : "Business Free"}</p>
            {isTrial ? (
              <p className="text-sm text-indigo-700" data-testid="plan-card-trial">Business Pro trial — {b.days_remaining} day{b.days_remaining === 1 ? "" : "s"} remaining</p>
            ) : b.status === "complimentary_pro" ? (
              <p className="text-sm text-indigo-700">Complimentary Pro — enjoy all Pro features.</p>
            ) : isPro ? (
              <p className="text-sm text-indigo-700">All Pro features active.</p>
            ) : (
              <p className="text-sm text-zinc-500">1 active opportunity · 3 invitations per opportunity.</p>
            )}
          </div>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${isPro ? "bg-indigo-100 text-indigo-700" : "bg-zinc-100 text-zinc-600"}`}>{b.status.replace(/_/g, " ")}</span>
      </div>

      {isTrial && (
        <div className="mt-3 flex items-center gap-2 rounded-xl bg-white/70 px-3 py-2 text-sm text-zinc-600" data-testid="plan-card-trial-note">
          <Clock className="h-4 w-4 text-indigo-500" />
          Enjoy all Pro features free until {fmtDate(b.trial_expiry)}. No card required.
        </div>
      )}

      <Button data-testid="plan-card-marketing" onClick={() => navigate("/business/marketing")}
        className={`mt-3 w-full rounded-full ${isPro ? "bg-indigo-600 hover:bg-indigo-700" : "bg-zinc-200 text-zinc-700 hover:bg-zinc-300"}`}>
        <Wand2 className="mr-1.5 h-4 w-4" /> {isPro ? "Open AI Marketing Assistant" : "Explore the AI Marketing Assistant"}
      </Button>

      {!isPro && (
        <ul className="mt-3 space-y-1 text-sm text-zinc-600">
          <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-600" /> Receive and manage creator applications</li>
          <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-600" /> 1 active opportunity · up to 3 invitations each</li>
        </ul>
      )}

      {/* Upgrade options — informational only until payments are live */}
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        <div className="rounded-xl border border-zinc-200 bg-white p-3">
          <p className="text-sm font-semibold text-zinc-900">Monthly</p>
          <p className="text-xl font-bold text-zinc-900">{price.currency_symbol}{price.monthly_price}<span className="text-sm font-normal text-zinc-500"> / month</span></p>
        </div>
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3">
          <p className="text-sm font-semibold text-zinc-900">Annual</p>
          <p className="text-xl font-bold text-zinc-900">{price.currency_symbol}{price.annual_price}<span className="text-sm font-normal text-zinc-500"> / year</span></p>
          <p className="mt-0.5 text-xs font-semibold text-indigo-700">{price.annual_saving_label}</p>
        </div>
      </div>
      <Button data-testid="plan-card-upgrade" disabled className="mt-3 w-full rounded-full bg-indigo-600 opacity-70 hover:bg-indigo-600" title="Coming soon">
        Upgrade to Pro — Coming soon
      </Button>
      <p className="mt-1 text-center text-xs text-zinc-400">Payments aren’t enabled yet — no charge will be taken.</p>
    </div>
  );
}
